import fetcher from 'api/fetcher';
import { getBannerCrop } from 'utils/banner/utils';
import {
    getFileName,
    getInputCropTypes,
    getLoadingCropType,
    resetImageComponentByUpscaleStatus,
    resetUpscaleStatus,
    sleep,
} from 'utils/clickme';
import {
    AiImageQueueStatus,
    AiImageQueueType,
    BANNER_IMAGE_CROP_TYPES_CONFIG,
    BannerImageCropType,
    CROP_CONTAINER_SIZE,
    ImageComponent,
    UpscaleStatus,
} from 'utils/constants/banner';
import { MagritteNotificationName } from 'utils/constants/notification';

import {
    AiImageQueuePosition,
    ImageInfo,
    ImageInfoResponse,
    setBannerImageAiImageQueueIdByType,
    setBannerImageAiImageQueuePositionByType,
    setBannerImageByType,
    setBannerImageComponentByType,
    setBannerImageUpscaleStatusByType,
    setBannerUpscaledImageByType,
} from 'store/models/createBanner';
import { addMagritteNotification } from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

const FETCH_INTERVAL = 5000;

type AiImageQueueInfoResponse = {
    status: AiImageQueueStatus;
    position: AiImageQueuePosition | null;
    resultImage: ImageInfoResponse;
};

type CheckUpscaleStatus = (
    cropType: BannerImageCropType,
    aiImageQueueId: number,
    upscaleStatus: UpscaleStatus,
    queueType: AiImageQueueType
) => AppThunkAction<void>;

type FetchUpscale = (
    originalImageId: number,
    cropType: BannerImageCropType,
    queueType: AiImageQueueType
) => AppThunkAction;

const getUpscaleImageInfo = (
    resultImage: ImageInfoResponse,
    imageCropType: BannerImageCropType,
    upscaleStatus: UpscaleStatus
): Partial<ImageInfo> => ({
    ...resultImage,
    fileName: getFileName(resultImage.id, resultImage.url),
    component: ImageComponent.Crop,
    cropInfo: getBannerCrop({
        cropContainerSize: CROP_CONTAINER_SIZE,
        imageWidth: resultImage.width,
        imageHeight: resultImage.height,
        cropAspect: BANNER_IMAGE_CROP_TYPES_CONFIG[imageCropType].aspect,
    }),
    isLoadedInCrop: false,
    upscaleStatus: upscaleStatus === UpscaleStatus.None ? UpscaleStatus.Upscale : UpscaleStatus.UpscaleAndFill,
    aiImageQueueId: null,
});

const checkUpscaleStatus: CheckUpscaleStatus = (cropType, aiImageQueueId, upscaleStatus, queueType) => {
    return (dispatch, getState): void => {
        const images = getState().createBanner.images;

        const getUpscaleStatus = async (): Promise<AiImageQueueInfoResponse | null> => {
            try {
                const response = await fetcher.get<unknown, AiImageQueueInfoResponse>(
                    `/api/v1/images/ai/${aiImageQueueId}/info`
                );

                const { status, position } = response;

                const currentAiImageQueueId = getState().createBanner.images[cropType].aiImageQueueId;
                const loadingCropType = getLoadingCropType(images);

                if (
                    aiImageQueueId !== currentAiImageQueueId ||
                    !loadingCropType ||
                    !aiImageQueueId ||
                    !currentAiImageQueueId
                ) {
                    return null;
                }

                dispatch(setBannerImageAiImageQueuePositionByType({ cropType, position }));

                if (status === AiImageQueueStatus.New || status === AiImageQueueStatus.InProgress) {
                    await sleep(FETCH_INTERVAL);
                    return await getUpscaleStatus();
                }

                return response;
            } catch {
                dispatch([
                    setBannerImageComponentByType({
                        cropType,
                        component: resetImageComponentByUpscaleStatus(upscaleStatus),
                    }),
                    setBannerImageUpscaleStatusByType({
                        cropType,
                        upscaleStatus: resetUpscaleStatus(upscaleStatus),
                    }),
                    addMagritteNotification(MagritteNotificationName.ImageUpscaleInfoSomethingWentWrong),
                ]);
                return null;
            }
        };

        void getUpscaleStatus().then((response) => {
            if (!response) {
                return;
            }

            const { status, resultImage } = response;

            const inputCropTypes = getInputCropTypes(images);

            if (status === AiImageQueueStatus.Success && !!resultImage) {
                const currentCropTypes = resultImage.availableCropsForImage.filter((imageCropType) =>
                    inputCropTypes.includes(imageCropType)
                );

                currentCropTypes.push(cropType);

                currentCropTypes.forEach((imageCropType) => {
                    const imageInfo = getUpscaleImageInfo(resultImage, imageCropType, upscaleStatus);

                    dispatch(
                        setBannerImageByType({
                            cropType: imageCropType,
                            imageInfo,
                        })
                    );

                    if (queueType === AiImageQueueType.Upscale) {
                        dispatch(
                            setBannerUpscaledImageByType({
                                cropType: imageCropType,
                                imageInfo,
                            })
                        );
                    }
                });
            }

            if (status === AiImageQueueStatus.Cancelled || status === AiImageQueueStatus.Error) {
                dispatch([
                    setBannerImageComponentByType({
                        cropType,
                        component: resetImageComponentByUpscaleStatus(upscaleStatus),
                    }),
                    setBannerImageUpscaleStatusByType({
                        cropType,
                        upscaleStatus: resetUpscaleStatus(upscaleStatus),
                    }),
                    setBannerImageAiImageQueueIdByType({ cropType, aiImageQueueId: null }),
                    setBannerImageAiImageQueuePositionByType({ cropType, position: null }),
                ]);

                if (status === AiImageQueueStatus.Error) {
                    dispatch(addMagritteNotification(MagritteNotificationName.ImageUpscaleInfoSomethingWentWrong));
                }
            }
        });
    };
};

export const fetchUpscale: FetchUpscale = (originalImageId, cropType, queueType) => {
    return (dispatch, getState): Promise<void> => {
        const images = getState().createBanner.images;
        const upscaleStatus = images[cropType].upscaleStatus as UpscaleStatus;

        return fetcher
            .post<unknown, { aiImageQueueId: number }>('/api/v1/images/ai/upscale', {
                originalImageId,
                imageCropType: upscaleStatus === UpscaleStatus.None ? BannerImageCropType.YandexAndVK : cropType,
                queueType,
            })
            .then((response) => {
                const { aiImageQueueId } = response;
                dispatch(setBannerImageAiImageQueueIdByType({ aiImageQueueId, cropType }));
                dispatch(checkUpscaleStatus(cropType, aiImageQueueId, upscaleStatus, queueType));
            })
            .catch(() => {
                dispatch([
                    setBannerImageComponentByType({
                        cropType,
                        component: resetImageComponentByUpscaleStatus(upscaleStatus),
                    }),
                    setBannerImageUpscaleStatusByType({
                        cropType,
                        upscaleStatus: resetUpscaleStatus(upscaleStatus),
                    }),
                    addMagritteNotification(MagritteNotificationName.ImageUpscaleSomethingWentWrong),
                ]);
            });
    };
};
